export function createDashboardPage({ initializeCalendar, renderEvents, getCache, setCache }) {
  let root = null;
  let organization = '';
  let calendar = null;
  let needsCalendar = true;
  let revision = 0;
  let timer = null;
  let pending = null;
  let request = null;

  function destroyCalendar() {
    if (window.WingmanState?.eventcalendarObj === calendar) {
      window.WingmanState.eventcalendarObj = null;
    }
    if (calendar) {
      try {
        calendar.destroy();
      } catch (error) {
        console.error('Unable to dispose dashboard calendar', error);
      }
    }
    calendar = null;
  }

  function stop() {
    ++revision;
    clearTimeout(timer);
    clearTimeout(pending);
    request?.abort();
    request = null;
    destroyCalendar();
    root = null;
    organization = '';
    needsCalendar = true;
  }

  function showError(target, message, retry) {
    let feedback = target.querySelector('#dashboard-feedback');
    if (!feedback) {
      feedback = document.createElement('div');
      feedback.id = 'dashboard-feedback';
      feedback.className = 'alert alert-danger';
      feedback.setAttribute('role', 'alert');
      target.prepend(feedback);
    }
    feedback.replaceChildren();
    const text = document.createElement('span');
    text.textContent = message;
    feedback.appendChild(text);
    if (retry) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn btn-secondary ms-3';
      button.textContent = 'Retry';
      button.onclick = retry;
      feedback.appendChild(button);
    }
  }

  async function load(version, options) {
    const target = root;
    const owner = organization;
    const current = () => revision === version
      && target === document.getElementById('area-date-events')
      && document.getElementById('customer_id')?.value === owner;
    if (!current()) return;
    const controller = new AbortController();
    request = controller;
    let succeeded = false;
    const loader = target.querySelector('#calendar_loading');
    try {
      if (!loader || !target.querySelector('#eventcalendar')
        || !document.getElementById('search_events')) {
        throw new Error('The dashboard is not ready. Retry loading it.');
      }
      loader.classList.remove('hide');
      if (needsCalendar) {
        destroyCalendar();
        calendar = initializeCalendar();
        needsCalendar = false;
      }
      const text = options.text || document.getElementById('search_events').value;
      let statuses = '/active,draft_suggested_active/';
      if (options.filters?.includes('archived')) {
        statuses = '/active,draft_suggested_active,archived_unknown,archived_resolved,archived_notanissue/';
      }
      if (options.filters?.includes('*')) statuses = '';
      const url = '/xapi/events' + statuses + '?start=' + window.get_start_date.format('YYYY-MM-DD')
        + '&end=' + window.get_end_date.format('YYYY-MM-DD');
      const cacheKey = owner + '_eventURL';
      const eventsKey = owner + '_dashboard_events';
      let data = getCache(cacheKey) === url && !options.bustCache ? getCache(eventsKey) : null;
      if (!Array.isArray(data?.events) || !data.events.length) {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!current()) return;
        if (response.status === 401) {
          showError(target, 'Your session has expired. Redirecting to sign in.');
          loader.classList.add('hide');
          stop();
          window.location.assign('/');
          return;
        }
        if (!response.ok) throw new Error('Unable to load dashboard events (HTTP ' + response.status + ').');
        data = await response.json();
        if (!current()) return;
        if (data?.status === 'fail' || !Array.isArray(data?.events)) {
          throw new Error(data?.msg || 'The server returned invalid dashboard events.');
        }
        setCache(eventsKey, data);
        setCache(cacheKey, url);
      }
      if (!current()) return;
      renderEvents(calendar, data, text);
      target.querySelector('#dashboard-feedback')?.remove();
      succeeded = true;
    } catch (error) {
      if (!current() || controller.signal.aborted) return;
      console.error('Dashboard load failed', error);
      showError(target, error.message || 'Unable to load dashboard events.', () => {
        refresh({ ...options, rebuild: true, bustCache: true });
      });
    } finally {
      if (current()) {
        loader?.classList.add('hide');
        request = null;
        if (succeeded && document.getElementById('today_selected')?.value === 'true') {
          timer = setTimeout(() => {
            if (document.getElementById('today_selected')?.value === 'true') {
              refresh({ ...options, bustCache: true });
            }
          }, 60000);
        }
      }
    }
  }

  function refresh({ rebuild = false, text = '', filters = null, bustCache = false } = {}) {
    const target = document.getElementById('area-date-events');
    const owner = document.getElementById('customer_id')?.value;
    if (!target || !owner) {
      stop();
      return;
    }
    if (root !== target || organization !== owner) {
      stop();
      root = target;
      organization = owner;
    }
    needsCalendar ||= rebuild;
    clearTimeout(timer);
    clearTimeout(pending);
    request?.abort();
    const version = ++revision;
    pending = setTimeout(() => load(version, { text, filters, bustCache }), 200);
  }

  new MutationObserver(() => {
    if (root && (root !== document.getElementById('area-date-events')
      || organization !== document.getElementById('customer_id')?.value)) {
      stop();
    }
  }).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('popstate', stop);
  window.addEventListener('pagehide', stop);
  window.addEventListener('pageshow', event => {
    if (event.persisted) refresh({ rebuild: true, bustCache: true });
  });
  return { refresh, stop };
}
